<script setup>
import { computed, ref } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { isActiveWebBia } from 'dashboard/composables/useBiaControl';

const props = defineProps({
  control: {
    type: Object,
    required: true,
  },
  showManualBanner: {
    type: Boolean,
    default: false,
  },
  publicReplyBlocked: {
    type: Boolean,
    default: false,
  },
});

const confirmationModal = ref(null);
const status = computed(() => props.control.status.value || {});
const isUnavailable = computed(
  () => props.control.isLoading.value || !status.value.available
);
const statusLabel = computed(() => {
  if (props.control.isLoading.value) return 'BIA_CONTROL.LOADING';
  if (!status.value.available) return 'BIA_CONTROL.UNAVAILABLE';
  if (
    status.value.lifecycle === 'human' ||
    status.value.lifecycle === 'closed' ||
    status.value.mode === 'human' ||
    status.value.mode === 'closed'
  ) {
    return 'BIA_CONTROL.HUMAN';
  }
  if (
    !status.value.can_pause &&
    !status.value.can_resume &&
    !status.value.can_takeover
  ) {
    return 'BIA_CONTROL.HUMAN';
  }
  if (status.value.paused === true) return 'BIA_CONTROL.PAUSED';
  if (status.value.paused === false) return 'BIA_CONTROL.ACTIVE';
  return 'BIA_CONTROL.UNAVAILABLE';
});
const activeWeb = computed(() => isActiveWebBia(status.value));

const pause = () => props.control.pause();
const resume = () => props.control.resume();
const takeover = async () => {
  const confirmed = await confirmationModal.value?.showConfirmation();
  if (confirmed) await props.control.takeover();
};
</script>

<template>
  <div
    class="flex w-full flex-wrap items-center justify-start gap-2 ps-3 pe-3"
    data-testid="bia-control"
  >
    <span
      class="text-xs font-medium text-n-slate-11"
      :class="{ 'text-n-ruby-11': isUnavailable }"
      data-testid="bia-control-status"
    >
      <!-- eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys -->
      {{ $t(statusLabel) }}
    </span>
    <NextButton
      v-if="status.can_pause"
      size="xs"
      variant="solid"
      color="blue"
      icon="i-lucide-play"
      :aria-label="$t('BIA_CONTROL.PAUSE')"
      :title="$t('BIA_CONTROL.PAUSE')"
      :is-loading="control.isActing.value"
      :disabled="
        control.isActing.value || control.isLoading.value || !status.available
      "
      data-testid="bia-control-pause"
      @click="pause"
    />
    <NextButton
      v-if="
        status.can_resume &&
        status.lifecycle !== 'human' &&
        status.lifecycle !== 'closed' &&
        status.mode !== 'human' &&
        status.mode !== 'closed'
      "
      size="xs"
      variant="solid"
      color="amber"
      icon="i-lucide-pause"
      :aria-label="$t('BIA_CONTROL.PLAY')"
      :title="$t('BIA_CONTROL.PLAY')"
      :is-loading="control.isActing.value"
      :disabled="
        control.isActing.value || control.isLoading.value || !status.available
      "
      data-testid="bia-control-resume"
      @click="resume"
    />
    <NextButton
      v-if="
        status.can_takeover &&
        status.channel !== 'web' &&
        status.channel !== 'whatsapp'
      "
      size="xs"
      variant="outline"
      color="ruby"
      :label="$t('BIA_CONTROL.TAKEOVER')"
      :is-loading="control.isActing.value"
      :disabled="
        control.isActing.value || control.isLoading.value || !status.available
      "
      data-testid="bia-control-takeover"
      @click="takeover"
    />
    <div
      class="basis-full rounded-lg bg-n-amber-3 px-3 py-2 text-xs text-n-amber-12"
      :class="{
        'invisible pointer-events-none': !(
          showManualBanner &&
          (activeWeb ||
            (status.channel === 'whatsapp' &&
              status.paused === false &&
              status.can_pause) ||
            publicReplyBlocked)
        ),
      }"
      :aria-hidden="
        !(
          showManualBanner &&
          (activeWeb ||
            (status.channel === 'whatsapp' &&
              status.paused === false &&
              status.can_pause) ||
            publicReplyBlocked)
        )
      "
      data-testid="bia-control-manual-banner"
    >
      {{ $t('BIA_CONTROL.ACTIVE_WEB_REPLY_BLOCKED') }}
    </div>
    <woot-confirm-modal
      ref="confirmationModal"
      :title="$t('BIA_CONTROL.TAKEOVER_CONFIRM_TITLE')"
      :description="$t('BIA_CONTROL.TAKEOVER_CONFIRM_DESCRIPTION')"
      :confirm-label="$t('BIA_CONTROL.TAKEOVER')"
      :cancel-label="$t('BIA_CONTROL.CANCEL')"
    />
  </div>
</template>
